import { create } from 'zustand';
import type { DateWindow } from '@/utils';

export type ProductsDateField = 'startDate' | 'created';

export const PRODUCTS_DATE_FIELD_LABELS: Record<ProductsDateField, string> = {
  startDate: 'Start date',
  created: 'Created',
};

export interface ProductsListFilters extends DateWindow {
  /**
   * Which loan date the window applies to: the AEST start date (the column,
   * and the field GET /loans sorts on) or the record's creation time. Sent
   * as GET /loans `dateField` whenever a side of the window is set.
   */
  dateField: ProductsDateField;
}

export interface ClientsListFilters extends DateWindow {
  /**
   * Status pick; '' = all. A GET /customers `status` value (incl.
   * NO_ACTIVITY) or DUPLICATE_ACCOUNT, which the page sends as the separate
   * `duplicate=true` filter — the BE keeps a duplicate's own status.
   */
  status: string;
}

/**
 * Status pick of the Assessments list; '' = all. The BE enum also has
 * `pending`, which the page does not offer (ticket: Failed / Approved; the
 * one pending row on dev has no statement and 500s the list).
 */
export type AssessmentsStatusFilter = '' | 'approved' | 'failed';

export interface AssessmentsListFilters extends DateWindow {
  /** Sent as GET /assessments `status` when set. */
  status: AssessmentsStatusFilter;
}

const DEFAULT_PRODUCTS: ProductsListFilters = {
  dateField: 'startDate',
  from: '',
  to: '',
};
const DEFAULT_CLIENTS: ClientsListFilters = { from: '', to: '', status: '' };
const DEFAULT_ASSESSMENTS: AssessmentsListFilters = {
  from: '',
  to: '',
  status: '',
};

export interface ContractActionsListFilters {
  /** GET /notes `isDone`: false = Unresolved (default), true = Resolved. */
  view: 'unresolved' | 'resolved';
  /** 'all' or one ContractActionType, sent as GET /notes `contractActionType`. */
  type: string;
}

const DEFAULT_CONTRACT_ACTIONS: ContractActionsListFilters = {
  view: 'unresolved',
  type: 'all',
};

interface MasterListFiltersState {
  products: ProductsListFilters;
  clients: ClientsListFilters;
  contractActions: ContractActionsListFilters;
  assessments: AssessmentsListFilters;
  setProducts: (patch: Partial<ProductsListFilters>) => void;
  clearProducts: () => void;
  setClients: (patch: Partial<ClientsListFilters>) => void;
  clearClients: () => void;
  setContractActions: (patch: Partial<ContractActionsListFilters>) => void;
  setAssessments: (patch: Partial<AssessmentsListFilters>) => void;
  clearAssessments: () => void;
}

/**
 * Date/status filters for the Products, Clients and Assessments master lists.
 *
 * Staff draw a "from go-live" line and then click into record after record;
 * the bare `/products` / `/clients` routes they come back to (tab close,
 * sidebar, breadcrumb) would reset component state on every return, so —
 * like the header search (clientSearch.store) — the filters live here for
 * the session. Deliberately not persisted: a reload or a new tab starts
 * unfiltered.
 */
export const useMasterListFiltersStore = create<MasterListFiltersState>(
  set => ({
    products: DEFAULT_PRODUCTS,
    clients: DEFAULT_CLIENTS,
    setProducts: patch =>
      set(state => ({ products: { ...state.products, ...patch } })),
    clearProducts: () => set({ products: DEFAULT_PRODUCTS }),
    setClients: patch =>
      set(state => ({ clients: { ...state.clients, ...patch } })),
    clearClients: () => set({ clients: DEFAULT_CLIENTS }),
    contractActions: DEFAULT_CONTRACT_ACTIONS,
    setContractActions: patch =>
      set(state => ({
        contractActions: { ...state.contractActions, ...patch },
      })),
    assessments: DEFAULT_ASSESSMENTS,
    setAssessments: patch =>
      set(state => ({ assessments: { ...state.assessments, ...patch } })),
    clearAssessments: () => set({ assessments: DEFAULT_ASSESSMENTS }),
  })
);
